import { RefreshCw, ExternalLink, Lock } from "lucide-react";

/** "23m ago" / "4h ago" / "yesterday · 14:32" / "23 Jun · 09:15" formatter so
 *  users can immediately tell stale headlines from fresh ones. */
function formatPublished(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const diffMin = Math.floor((now - d) / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 12) return `${diffHr}h ago`;
  const sameDay = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (sameDay) return `today · ${time}`;
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return `yesterday · ${time}`;
  return d.toLocaleDateString([], { day: "2-digit", month: "short" }) + ` · ` +
         d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

function isStaleNews(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return (Date.now() - d.getTime()) > 24 * 60 * 60 * 1000;
}

function sentimentDot(s) {
  if (s === "bullish") return "bg-[#22C55E]";
  if (s === "bearish") return "bg-[#EF4444]";
  return "bg-zinc-500";
}

function NewsCard({ n, idx, isPro, isAdmin }) {
  const hasUrl = n.url && n.url !== "#";
  return (
    <div className="border border-zinc-900 hover:border-zinc-700 p-3 transition-colors flex flex-col" data-testid={`news-item-${idx}`}>
      <div className="flex items-start gap-2">
        <span className={`h-2 w-2 mt-1.5 ${(isPro || isAdmin) ? sentimentDot(n.sentiment) : "bg-zinc-700"} shrink-0`} />
        <div className="flex-1 min-w-0">
          {hasUrl ? (
            <a href={n.url} target="_blank" rel="noreferrer"
               className="block text-sm font-medium leading-snug hover:text-[#007AFF] transition-colors group"
               data-testid={`news-title-${idx}`}>
              {n.title}
              <ExternalLink className="inline-block h-3 w-3 ml-1 -mt-0.5 opacity-50 group-hover:opacity-100" />
            </a>
          ) : (
            <div className="text-sm font-medium leading-snug" data-testid={`news-title-${idx}`}>{n.title}</div>
          )}
          {n.published && (
            <div className="mt-1.5 flex items-center gap-2 text-[10px] uppercase tracking-widest font-mono">
              <span
                className={`whitespace-nowrap ${isStaleNews(n.published) ? "text-[#F59E0B] font-bold" : "text-zinc-500"}`}
                data-testid={`news-published-${idx}`}
                title={new Date(n.published).toLocaleString()}
              >
                {formatPublished(n.published)}
              </span>
              {isStaleNews(n.published) && (
                <span className="text-[9px] px-1.5 py-0.5 bg-[#F59E0B]/15 text-[#F59E0B] border border-[#F59E0B]/40">stale</span>
              )}
            </div>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
            <span data-testid={`news-source-${idx}`} className="whitespace-nowrap">{n.source}</span>
            {(isPro || isAdmin) ? (
              <>
                <span className="text-zinc-700">·</span>
                <span className={n.sentiment === "bullish" ? "text-[#22C55E]" : n.sentiment === "bearish" ? "text-[#EF4444]" : "text-zinc-400"}>{n.sentiment}</span>
                <span className="text-zinc-700">·</span>
                <span>{n.impact} impact</span>
                <span className="text-zinc-700">·</span>
                <span>{n.confidence}%</span>
              </>
            ) : (
              <>
                <span className="text-zinc-700">·</span>
                <span className="text-zinc-700 inline-flex items-center gap-1"><Lock className="h-3 w-3" /> AI sentiment locked</span>
              </>
            )}
          </div>
          {(isPro || isAdmin) && (
            <div className="mt-2 text-xs text-zinc-400 leading-relaxed line-clamp-2" data-testid={`news-reason-${idx}`}>
              {n.reason}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const NewsPanel = ({ news, loadingNews, newsLoadingMsg, selected, isPro, isAdmin, onRefresh }) => {
  // Declutter: only last 48h; prefer HIGH-impact. If no high-impact items,
  // show up to 3 recent ones so the panel isn't dead. Cap at 6 cards.
  let visible = [];
  let fallback = false;
  if (news?.items?.length > 0) {
    const cutoff = Date.now() - 48 * 3600 * 1000;
    const fresh = [...news.items]
      .filter((n) => n.published && new Date(n.published).getTime() >= cutoff)
      .sort((a, b) => new Date(b.published).getTime() - new Date(a.published).getTime());
    visible = fresh.filter((n) => n.impact === "high");
    if (visible.length === 0) { visible = fresh.slice(0, 3); fallback = true; }
    visible = visible.slice(0, 6);
  }

  return (
    <section className="bg-[#121212] border border-zinc-800 p-4" data-testid="news-panel">
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-zinc-800">
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500">Live news impact</div>
          <div className="font-heading text-lg font-bold uppercase tracking-tight">{selected?.symbol} signals</div>
        </div>
        {isAdmin && (
          <button onClick={onRefresh} className="text-zinc-500 hover:text-white p-1" data-testid="news-refresh">
            <RefreshCw className={`h-4 w-4 ${loadingNews ? "animate-spin" : ""}`} />
          </button>
        )}
      </div>

      {loadingNews && <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest py-8 text-center">◢ {newsLoadingMsg}</div>}

      {news?.items?.length > 0 && visible.length === 0 && (
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 py-6 text-center" data-testid="news-quiet">
          No major market-moving news in the last 48h — quiet tape.
        </div>
      )}

      {visible.length > 0 && (
        <>
          <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-600 mb-2" data-testid="news-filter-note">
            {fallback ? "No high-impact headlines — showing latest (48h)" : "High-impact only · last 48h"}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {visible.map((n, idx) => (
              <NewsCard key={n.url && n.url !== "#" ? n.url : `${n.title}-${n.published}`} n={n} idx={idx} isPro={isPro} isAdmin={isAdmin} />
            ))}
          </div>
        </>
      )}
    </section>
  );
};
